import { useState, useCallback } from 'react';

interface CopyButtonProps {
    getText: () => string;
    title?: string;
    className?: string;
}

export default function CopyButton({ getText, title = 'Copy to clipboard', className = '' }: CopyButtonProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(getText());
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = getText();
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    }, [getText]);

    return (
        <button
            onClick={handleCopy}
            className={`p-1 rounded hover:bg-slate-200/60 transition-colors ${className}`}
            title={title}
        >
            {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
            )}
        </button>
    );
}

interface CopyImageButtonProps {
    getElement: () => HTMLElement | null;
    title?: string;
    className?: string;
}

export function CopyImageButton({ getElement, title = 'Copy as image', className = '' }: CopyImageButtonProps) {
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        const el = getElement();
        if (!el) return;
        setLoading(true);
        try {
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(el);
            const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve));
            if (blob) {
                await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }
        } catch (err) {
            console.error('Failed to copy chart as image:', err);
        } finally {
            setLoading(false);
        }
    }, [getElement]);

    return (
        <button
            onClick={handleCopy}
            className={`p-1 rounded hover:bg-slate-200/60 transition-colors ${className}`}
            title={title}
        >
            {loading ? (
                <div className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
            ) : copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
            )}
        </button>
    );
}
